import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { ClipboardList } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatNumber }   from "@/utils/format";
import { StatusStat }     from "../chartUtils";
import { PedidosModalContent, ModalEntityTitle } from "./PedidosModalContent";
import { PieActiveSector } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { payload: StatusStat }[];
}
function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-3 h-3 rounded-full" style={{ background: d.fill }} />
        <span className="text-sm font-semibold text-slate-800">{d.name}</span>
      </div>
      <p className="text-sm text-slate-600">
        {formatNumber(d.quantidade)} pedido{d.quantidade !== 1 ? "s" : ""}
      </p>
      <p className="text-[10px] text-slate-400 mt-1.5">Clique para ver detalhes</p>
    </div>
  );
}

/* ─── Label no centro da fatia ───────────────────────────────────────────── */
const RADIAN = Math.PI / 180;

function SliceLabel(props: Record<string, unknown>) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props as {
    cx: number; cy: number; midAngle: number;
    innerRadius: number; outerRadius: number; percent: number;
  };
  if ((percent ?? 0) < 0.07) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
      fill="white" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props { data: StatusStat[]; loading: boolean; }

export function PedidosPendentes({ data, loading }: Props) {
  const [selected, setSelected] = useState<StatusStat | null>(null);

  const total       = data.reduce((s, d) => s + d.quantidade, 0);
  const pctPendente = total > 0
    ? ((data.find((d) => d.name === "Pendente")?.quantidade ?? 0) / total) * 100
    : 0;

  return (
    <>
      <ChartContainer
        title="% Pedidos Pendentes"
        subtitle={`${formatNumber(pctPendente, 1)}% aguardam processamento`}
        loading={loading}
        empty={!loading && data.length === 0}
        height={280}
      >
        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Pie
            data={data}
            cx="50%"
            cy="46%"
            innerRadius="50%"
            outerRadius="75%"
            dataKey="value"
            nameKey="name"
            paddingAngle={3}
            labelLine={false}
            label={(p) => <SliceLabel {...(p as unknown as Record<string, unknown>)} />}
            cursor="pointer"
            activeShape={PieActiveSector}
            onClick={(d: unknown) => setSelected(d as StatusStat)}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.fill} stroke="white" strokeWidth={2} />
            ))}
          </Pie>

          <Tooltip content={<CustomTooltip />} />

          <Legend
            iconType="circle"
            iconSize={9}
            wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
            formatter={(value: string) => (
              <span style={{ color: "#475569" }}>
                {value} ({formatNumber(data.find((d) => d.name === value)?.quantidade ?? 0)})
              </span>
            )}
          />

          {/* Total no centro */}
          <text x="50%" y="42%" textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 22, fontWeight: 700, fill: "#1e293b" }}>
            {formatNumber(total)}
          </text>
          <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 11, fill: "#94a3b8" }}>
            pedidos
          </text>
        </PieChart>
      </ChartContainer>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <ModalEntityTitle
            icon={<ClipboardList className="w-4 h-4" />}
            label={`Pedidos ${selected.name}`}
            iconBg="bg-slate-100"
            iconColor="text-slate-600"
          />
        )}
      >
        {selected && (
          <PedidosModalContent
            pedidos={selected.pedidos}
            expandable
            banner={{
              label: selected.name,
              value: `${formatNumber(selected.quantidade)} pedido${selected.quantidade !== 1 ? "s" : ""}`,
              color: selected.fill,
            }}
          />
        )}
      </Modal>
    </>
  );
}
